import { redirect } from '@tanstack/react-router'

export type GuardDecision = 'ok' | 'mfa_enrollment' | 'login'

// readStoredSession reads the session flags the auth context persists in
// localStorage. An absent or unparsable entry means signed out.
export function readStoredSession(): { isAuthenticated: boolean; mfaEnrollmentRequired: boolean; orgCode: string | null } {
    let isAuthenticated = false
    let mfaEnrollmentRequired = false
    let orgCode: string | null = null
    try {
        const raw = localStorage.getItem('authState')
        if (raw) {
            const parsed = JSON.parse(raw)
            isAuthenticated = parsed.isAuthenticated === true
            mfaEnrollmentRequired = parsed.mfaEnrollmentRequired === true
            orgCode = typeof parsed.orgCode === 'string' && parsed.orgCode ? parsed.orgCode : null
        }
    } catch (error) {
        console.error('Failed to parse stored auth state:', error)
    }
    return { isAuthenticated, mfaEnrollmentRequired, orgCode }
}

// decideAuthGuard is the pure decision shared by the org-scoped layout and the
// org-less /organizations layout: a session restricted to MFA enrollment
// reaches nothing else, and a signed-out visitor goes to the login page.
export function decideAuthGuard(session: { isAuthenticated: boolean; mfaEnrollmentRequired: boolean }): GuardDecision {
    if (session.isAuthenticated && session.mfaEnrollmentRequired) return 'mfa_enrollment'
    if (!session.isAuthenticated) return 'login'
    return 'ok'
}

// requireSession throws the redirect a route's beforeLoad needs, or returns.
// `org` is the organization whose login page is used; org-less routes pass
// undefined and fall back to the stored session's org, then `demo`.
export function requireSession(org: string | undefined, nextHref: string): void {
    const session = readStoredSession()
    const decision = decideAuthGuard(session)
    const loginOrg = org || session.orgCode || 'demo'
    if (decision === 'mfa_enrollment') {
        throw redirect({ to: '/mfa-setup', search: { org: loginOrg, next: nextHref } })
    }
    if (decision === 'login') {
        throw redirect({
            to: '/$org/login',
            params: { org: loginOrg },
            search: { next: nextHref, email: undefined, password: undefined },
        })
    }
}
